import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import { IconEdit, IconTrash } from '../ui/icons';

function fingerprintForm(form) {
  return JSON.stringify({
    host: String(form?.host || ''),
    port: Number(form?.port || 0),
    db_name: String(form?.db_name || ''),
    username: String(form?.username || ''),
    has_password: Boolean(form?.has_password),
  });
}

export default function DatabasesPage({ siteId, site, onUnsavedChange }) {
  const [state, setState] = useState({ status: 'loading', error: '', saving: false, deleting: false, testing: false, test: null, connections: [] });
  const [form, setForm] = useState({ host: '', port: 3306, db_name: '', username: '', password: '', has_password: false });
  const [savedFingerprint, setSavedFingerprint] = useState('');
  const savedFormRef = useRef(null);
  const saveLatestRef = useRef(null);
  const discardLatestRef = useRef(null);
  const saveFromBar = useCallback(() => saveLatestRef.current?.(), []);
  const discardFromBar = useCallback(() => discardLatestRef.current?.(), []);
  const lastUnsavedRef = useRef({ dirty: null, label: '' });

  const isDirty = useMemo(() => {
    if (!savedFingerprint) return false;
    return fingerprintForm({ ...form, has_password: Boolean(form.password) || Boolean(form.has_password) }) !== savedFingerprint;
  }, [form, savedFingerprint]);

  useEffect(() => {
    const label = 'Bases de données';
    const dirty = Boolean(isDirty);
    if (lastUnsavedRef.current.dirty !== dirty || lastUnsavedRef.current.label !== label) {
      lastUnsavedRef.current = { dirty, label };
      onUnsavedChange?.({ dirty, label, onSave: saveFromBar, onDiscard: discardFromBar });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, saveFromBar, discardFromBar]);

  useEffect(() => {
    return () => onUnsavedChange?.({ dirty: false, label: '', onSave: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: '', test: null }));
    api
      .dbConnections(Number(siteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState((s) => ({ ...s, status: 'error', error: res.error || 'Impossible de charger', connections: [] }));
          return;
        }
        const list = res.connections || [];
        const first = list[0] || null;
        setState((s) => ({ ...s, status: 'ready', error: '', connections: list }));
        if (first) {
          const nextForm = {
            host: first.host || '',
            port: Number(first.port || 3306),
            db_name: first.db_name || '',
            username: first.username || '',
            password: '',
            has_password: Boolean(first.has_password),
          };
          setForm(nextForm);
          const fp = fingerprintForm(nextForm);
          setSavedFingerprint(fp);
          savedFormRef.current = nextForm;
        } else {
          const nextForm = { host: '', port: 3306, db_name: '', username: '', password: '', has_password: false };
          setForm(nextForm);
          const fp = fingerprintForm(nextForm);
          setSavedFingerprint(fp);
          savedFormRef.current = nextForm;
        }
      })
      .catch(() => {
        if (cancelled) return;
        setState((s) => ({ ...s, status: 'error', error: 'Impossible de charger', connections: [] }));
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  useEffect(() => refresh(), [refresh]);

  async function save() {
    if (!siteId) return;
    setState((s) => ({ ...s, saving: true, error: '' }));
    const res = await api.dbConnectionSave({
      site_id: Number(siteId),
      driver: 'mysql',
      host: form.host,
      port: Number(form.port),
      db_name: form.db_name,
      username: form.username,
      password: form.password,
    });
    if (!res.ok) {
      setState((s) => ({ ...s, saving: false, error: res.error || 'Enregistrement impossible' }));
      return res;
    }
    setForm((f) => ({ ...f, password: '', has_password: true }));
    setState((s) => ({ ...s, saving: false }));
    refresh();
    return res;
  }

  async function test(useStored) {
    if (!siteId) return;
    setState((s) => ({ ...s, testing: true, error: '' }));
    const payload = useStored
      ? { site_id: Number(siteId), use_stored: true }
      : {
          site_id: Number(siteId),
          use_stored: false,
          driver: 'mysql',
          host: form.host,
          port: Number(form.port),
          db_name: form.db_name,
          username: form.username,
          password: form.password,
        };
    const res = await api.dbConnectionTest(payload);
    setState((s) => ({ ...s, testing: false, test: res.ok ? res : { ok: false, error: res.error || 'Test impossible' } }));
    return res;
  }

  async function remove() {
    if (!siteId) return;
    if (!window.confirm('Supprimer la base enregistrée pour ce site ?')) return;
    setState((s) => ({ ...s, deleting: true, error: '' }));
    const res = await api.dbConnectionDelete({ site_id: Number(siteId) });
    if (!res.ok) {
      setState((s) => ({ ...s, deleting: false, error: res.error || 'Suppression impossible' }));
      return res;
    }
    setState((s) => ({ ...s, deleting: false }));
    refresh();
    return res;
  }

  saveLatestRef.current = save;
  discardLatestRef.current = () => {
    const saved = savedFormRef.current;
    if (!saved) return;
    setForm({ ...saved, password: '' });
    setSavedFingerprint(fingerprintForm(saved));
  };

  if (!siteId) {
    return (
      <div className="Page">
        <div className="Card">
          <div className="TileTitle">Bases de données</div>
          <div className="Muted">Sélectionne un site.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Bases de données' }]} />
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      <div className="Card">
        <div className="CardHeader">
          <div className="TitleLg">Connexion MySQL</div>
        </div>

        <div className="FormGrid">
          <div className="Field">
            <label className="Label">Host</label>
            <input className="Input" value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))} />
          </div>
          <div className="Field">
            <label className="Label">Port</label>
            <input className="Input" inputMode="numeric" value={form.port} onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))} />
          </div>
          <div className="Field">
            <label className="Label">Base</label>
            <input className="Input" value={form.db_name} onChange={(e) => setForm((f) => ({ ...f, db_name: e.target.value }))} />
          </div>
          <div className="Field">
            <label className="Label">Utilisateur</label>
            <input className="Input" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} />
          </div>
          <div className="Field" style={{ gridColumn: '1 / -1' }}>
            <label className="Label">Mot de passe {form.has_password ? '(enregistré)' : ''}</label>
            <input
              className="Input"
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder={form.has_password ? 'Laisse vide pour conserver' : 'Requis'}
            />
          </div>
        </div>

        <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 12 }}>
          <button type="button" className="Button Button--ghost" onClick={() => test(false)} disabled={state.testing}>
            Tester
          </button>
          <button type="button" className="Button Button--ghost" onClick={() => test(true)} disabled={state.testing || !form.has_password}>
            Tester (stockée)
          </button>
          <button type="button" className="Button" onClick={save} disabled={state.saving}>
            <span className="ButtonInline">
              <IconEdit /> {state.saving ? 'Enregistrement…' : 'Enregistrer'}
            </span>
          </button>
          <button type="button" className="Button Button--ghost Button--danger" onClick={remove} disabled={state.deleting || state.saving}>
            <span className="ButtonInline">
              <IconTrash /> Supprimer
            </span>
          </button>
        </div>

        {state.test ? (
          <div className="RoleBox" style={{ marginTop: 14 }}>
            {state.test.ok ? (
              <>
                <div className="TileTitle" style={{ marginBottom: 6 }}>
                  Connexion OK
                </div>
                <div className="Muted" style={{ marginBottom: 10 }}>
                  {state.test.db?.name} — {state.test.db?.host}:{state.test.db?.port} — MySQL {state.test.db?.version}
                </div>
                <div className="Muted">{Array.isArray(state.test.tables) ? `${state.test.tables.length} tables détectées` : ''}</div>
              </>
            ) : (
              <div className="Error">{state.test.error || 'Test impossible'}</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
